// STUDIO-LIVE-REAL-CONTROL-INTEGRATION-1 · 실제 방송 타이머 문구·외부공지 저장(판매자별) + 방송 제어 API 어댑터.
// LivePresetPanel / RemoteControlLayer 는 TEST 와 같은 컴포넌트를 쓰고, 여기서 실제 API 로만 연결한다.
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';

const errText = (e, fb) => {
  const d = e?.response?.data?.detail;
  return (d && typeof d === 'object' ? d.message : d) || fb;
};

export const useLivePresets = (enabled) => {
  const [st, setSt] = useState(null);
  const ref = useRef(null);
  ref.current = st;
  // 오래된 GET 응답이 최신 버전을 덮지 않도록 버전 비교.
  const accept = useCallback((data) => {
    if (data && (!ref.current || data.version >= ref.current.version)) setSt(data);
  }, []);
  const load = useCallback(async () => {
    const { data } = await api.get('/seller/live-presets');
    accept(data);
  }, [accept]);
  useEffect(() => {
    if (!enabled) return undefined;
    load().catch(() => {});
    const id = setInterval(() => { load().catch(() => {}); }, 4000);
    return () => clearInterval(id);
  }, [enabled, load]);
  const save = useCallback(async (next) => {
    const cur = ref.current;
    if (!cur) return;
    try {
      const { data } = await api.put('/seller/live-presets', {
        timer_presets: next.timer_presets, external_notices: next.external_notices, base_version: cur.version });
      accept(data);
    } catch (e) {
      const c = e?.response?.data?.detail?.current;
      if (c) setSt(c);
      toast.error(errText(e, '저장하지 못했어요.'));
    }
  }, [accept]);
  return { presets: st, save };
};

export const realTimer = async (scheduleId, action, extra = {}) => {
  if (!scheduleId) { toast.error('편성표 방송이 아니어서 타이머를 사용할 수 없어요.'); return false; }
  try { await api.post(`/seller/broadcasts/${scheduleId}/timer`, { action, ...extra }); return true; }
  catch (e) { toast.error(errText(e, '타이머 제어 실패')); return false; }
};

// n: 보관함 공지 {notice_id, text} | null(내리기) → 반환: 새 live_notice | null | undefined(실패)
export const realNotice = async (sessionId, n) => {
  if (!sessionId) { toast.error('방송 세션이 없어요.'); return undefined; }
  try {
    if (!n) { await api.delete(`/seller/streaming/sessions/${sessionId}/notice`); return null; }
    const { data } = await api.put(`/seller/streaming/sessions/${sessionId}/notice`, { text: n.text.trim(), notice_id: n.notice_id });
    return data.live_notice;
  } catch (e) { toast.error(errText(e, '공지 변경 실패')); return undefined; }
};

export const realExternal = async (sessionId, id) => {
  if (!sessionId) { toast.error('방송 세션이 없어요.'); return undefined; }
  try {
    if (!id) { await api.delete(`/seller/streaming/sessions/${sessionId}/external-notice`); return null; }
    const { data } = await api.put(`/seller/streaming/sessions/${sessionId}/external-notice`, { notice_id: id });
    return data.external_notice;
  } catch (e) { toast.error(errText(e, '외부공지 선택 실패')); return undefined; }
};

// LivePresetPanel 용 ctx · apply (목록 변경 → 판매자 저장 · 선택 변경 → 방송 외부공지 API).
export const realPresetCtx = (presets, selectedId, timer) => ({
  timer_presets: presets?.timer_presets || [], external_notices: presets?.external_notices || [],
  external_notice_selected: selectedId || null, timer: timer || null,
});

export const makeRealApply = ({ ctx, save, sessionId, onSelected }) => (fn) => {
  const next = fn(ctx);
  if (next.timer_presets !== ctx.timer_presets || next.external_notices !== ctx.external_notices) save(next);
  if (next.external_notice_selected !== ctx.external_notice_selected) {
    realExternal(sessionId, next.external_notice_selected).then((r) => { if (r !== undefined) onSelected?.(r); });
  }
};
